/**
 * Device Fingerprint SDK - Versão Refatorada
 * SDK modular para coleta de fingerprint de dispositivos
 */

// Core
export { FingerprintSDK } from './core/FingerprintSDK.js';

// Services
export {
    CanvasService,
    WebGLService,
    AudioService,
    BehavioralService,
    DeviceService,
    PrivacyService,
    NetworkService,
    FingerprintCollector,
    HttpService,
    ServiceFactory,
    ServiceUtils,
    SERVICE_NAMES,
    SERVICE_PRIORITIES,
    DEFAULT_SERVICE_CONFIG,
    SERVICE_METADATA
} from './services/index.js';

// Utils
export {
    ConfigValidator,
    Logger,
    HashUtils,
    PerformanceUtils,
    EnvironmentUtils,
    ValidationUtils,
    StringUtils,
    ArrayUtils,
    DateUtils,
    UTILS_CONSTANTS
} from './utils/index.js';

// Handlers
export {
    ErrorHandler,
    EventHandler,
    StateHandler,
    HANDLER_EVENTS,
    HANDLER_STATES
} from './handlers/index.js';

/**
 * Configuração padrão do SDK
 */
export const DEFAULT_SDK_CONFIG = {
    // Configurações básicas
    apiUrl: null,
    customerId: null,
    sessionId: null,
    autoSend: false,

    // Configurações de coleta
    collectCanvas: true,
    collectWebGL: true,
    collectAudio: true,
    collectDevice: true,
    collectPrivacy: true,
    collectNetwork: true,
    collectBehavioral: false,

    // Configurações comportamentais
    behavioralTimeout: 30000, // 30 segundos
    maxBehavioralEvents: 100,
    behavioralSampleRate: 0.1, // 10% dos eventos

    // Configurações de performance
    timeout: 10000, // 10 segundos
    parallel: true,
    maxRetries: 3,
    retryDelay: 1000,

    // Configurações de cache
    enableCache: true,
    cacheTimeout: 300000, // 5 minutos

    // Configurações de logging
    enableLogging: false,
    logLevel: 'error', // 'debug', 'info', 'warn', 'error'

    // Configurações de privacidade
    respectDoNotTrack: true,
    anonymizeIPs: true,

    // Configurações de qualidade
    minQualityScore: 0.7,
    enableQualityMetrics: true,

    // Configurações de segurança
    validateOrigin: true,
    enableCSP: true,

    // Configurações experimentais
    experimental: {
        enableAdvancedCanvas: false,
        enableWebGLExtensions: false,
        enableAudioAnalysis: false,
        enableNetworkTiming: false
    }
};

/**
 * Versão do SDK
 */
export const SDK_VERSION = '2.0.0';

/**
 * Metadados do SDK
 */
export const SDK_METADATA = {
    name: 'Device Fingerprint SDK',
    version: SDK_VERSION,
    author: 'MS Antifraude',
    description: 'SDK modular para coleta de fingerprint de dispositivos',
    license: 'MIT',
    repository: 'https://github.com/ms-antifraude/device-fingerprint-sdk',
    bugs: 'https://github.com/ms-antifraude/device-fingerprint-sdk/issues',
    homepage: 'https://ms-antifraude.github.io/device-fingerprint-sdk',
    keywords: ['fingerprint', 'device', 'fraud', 'detection', 'security'],
    engines: {
        browser: '>= 2015' // ES6+ support
    },
    features: {
        canvas: 'Canvas 2D fingerprinting',
        webgl: 'WebGL fingerprinting with extensions',
        audio: 'Web Audio API fingerprinting',
        device: 'Device and browser information',
        privacy: 'Privacy and security detection',
        network: 'Network analysis and geolocation',
        behavioral: 'User behavior analysis',
        modular: 'Modular architecture with services',
        performance: 'Performance optimized collection',
        security: 'Security-focused implementation'
    }
};

/**
 * Função de conveniência para criar instância do SDK
 */
export function createFingerprintSDK(config = {}) {
    const mergedConfig = {
        ...DEFAULT_SDK_CONFIG,
        ...config,
        experimental: {
            ...DEFAULT_SDK_CONFIG.experimental,
            ...(config.experimental || {})
        }
    };

    return new FingerprintSDK(mergedConfig);
}

/**
 * Função para verificar suporte do navegador
 */
export function checkBrowserSupport() {
    const support = {
        canvas: false,
        webgl: false,
        audio: false,
        crypto: false,
        performance: false,
        storage: false,
        workers: false,
        es6: false
    };

    try {
        // Canvas 2D
        const canvas = document.createElement('canvas');
        support.canvas = !!(canvas.getContext && canvas.getContext('2d'));

        // WebGL
        support.webgl = !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));

        // Web Audio API
        support.audio = !!(window.AudioContext || window.webkitAudioContext);

        // Crypto API
        support.crypto = !!(window.crypto && window.crypto.subtle);

        // Performance API
        support.performance = !!(window.performance && window.performance.now);

        // Local Storage
        support.storage = !!(window.localStorage);

        // Web Workers
        support.workers = !!(window.Worker);

        // ES6 Features
        support.es6 = !!(window.Promise && window.Map && window.Set);

    } catch (error) {
        console.warn('Erro ao verificar suporte do navegador:', error);
    }

    return support;
}

/**
 * Função para verificar se o SDK pode ser executado
 */
export function canRunSDK() {
    const support = checkBrowserSupport();

    // Requisitos mínimos
    const requirements = {
        canvas: support.canvas,
        crypto: support.crypto,
        es6: support.es6
    };

    const canRun = Object.values(requirements).every(Boolean);

    return {
        canRun,
        support,
        requirements,
        missing: Object.entries(requirements)
            .filter(([, supported]) => !supported)
            .map(([feature]) => feature)
    };
}

/**
 * Função para obter informações do ambiente
 */
export function getEnvironmentInfo() {
    const support = checkBrowserSupport();

    return {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        languages: navigator.languages || [navigator.language],
        cookieEnabled: navigator.cookieEnabled,
        doNotTrack: navigator.doNotTrack,
        onLine: navigator.onLine,
        hardwareConcurrency: navigator.hardwareConcurrency,
        maxTouchPoints: navigator.maxTouchPoints,
        screen: {
            width: screen.width,
            height: screen.height,
            availWidth: screen.availWidth,
            availHeight: screen.availHeight,
            colorDepth: screen.colorDepth,
            pixelDepth: screen.pixelDepth
        },
        window: {
            innerWidth: window.innerWidth,
            innerHeight: window.innerHeight,
            outerWidth: window.outerWidth,
            outerHeight: window.outerHeight,
            devicePixelRatio: window.devicePixelRatio
        },
        support,
        timestamp: Date.now(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        sdkVersion: SDK_VERSION
    };
}

/**
 * Função utilitária para debug
 */
export function debugSDK(sdk) {
    if (!sdk || typeof sdk.getStats !== 'function') {
        console.error('SDK inválido fornecido para debug');
        return null;
    }

    const stats = sdk.getStats();
    const config = sdk.getConfig();
    const environment = getEnvironmentInfo();

    const debugInfo = {
        sdk: {
            version: SDK_VERSION,
            status: sdk.isInitialized ? 'initialized' : 'not-initialized',
            stats,
            config: {
                ...config,
                // Remove informações sensíveis
                apiUrl: config.apiUrl ? '[HIDDEN]' : null,
                customerId: config.customerId ? '[HIDDEN]' : null
            }
        },
        environment,
        timestamp: Date.now()
    };

    console.group('🔍 SDK Debug Info');
    console.log('📊 Estatísticas:', stats);
    console.log('⚙️ Configuração:', debugInfo.sdk.config);
    console.log('🌐 Ambiente:', environment);
    console.log('📋 Info Completa:', debugInfo);
    console.groupEnd();

    return debugInfo;
}

/**
 * Exportação padrão
 */
export default {
    FingerprintSDK,
    createFingerprintSDK,
    checkBrowserSupport,
    canRunSDK,
    getEnvironmentInfo,
    debugSDK,
    DEFAULT_SDK_CONFIG,
    SDK_VERSION,
    SDK_METADATA
};

// Compatibilidade com CommonJS
if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        FingerprintSDK,
        createFingerprintSDK,
        checkBrowserSupport,
        canRunSDK,
        getEnvironmentInfo,
        debugSDK,
        DEFAULT_SDK_CONFIG,
        SDK_VERSION,
        SDK_METADATA,
        default: {
            FingerprintSDK,
            createFingerprintSDK,
            checkBrowserSupport,
            canRunSDK,
            getEnvironmentInfo,
            debugSDK,
            DEFAULT_SDK_CONFIG,
            SDK_VERSION,
            SDK_METADATA
        }
    };
}

// Compatibilidade com AMD
if (typeof define === 'function' && define.amd) {
    define([], function () {
        return {
            FingerprintSDK,
            createFingerprintSDK,
            checkBrowserSupport,
            canRunSDK,
            getEnvironmentInfo,
            debugSDK,
            DEFAULT_SDK_CONFIG,
            SDK_VERSION,
            SDK_METADATA
        };
    });
}

// Exposição global (para uso via script tag)
if (typeof window !== 'undefined') {
    window.DeviceFingerprintSDK = {
        FingerprintSDK,
        createFingerprintSDK,
        checkBrowserSupport,
        canRunSDK,
        getEnvironmentInfo,
        debugSDK,
        DEFAULT_SDK_CONFIG,
        SDK_VERSION,
        SDK_METADATA
    };
}